import {
  FORMA_CORTESIA,
  FORMA_DINHEIRO,
  FORMA_DOLAR,
  FORMA_EURO,
  FORMA_GUARANI,
  FORMA_PESO,
  FORMA_PESO_CHILENO,
  FORMA_PRAZO
} from 'consts';

/* Retorna a ISO da moeda e a abreviatura em objeto */
export function getISOMoeda(moeda) {
  let abrevMoeda = '';
  let isoMoeda = '';

  switch (parseInt(moeda)) {
    case FORMA_DINHEIRO:
      abrevMoeda = 'pt-BR';
      isoMoeda = 'BRL';

      break;
    case FORMA_DOLAR:
      abrevMoeda = 'en-US';
      isoMoeda = 'USD';

      break;
    case FORMA_EURO:
      abrevMoeda = 'en-US';
      isoMoeda = 'EUR';

      break;
    case FORMA_GUARANI:
      abrevMoeda = 'es-PY';
      isoMoeda = 'PYG';

      break;
    case FORMA_PESO:
      abrevMoeda = 'es-AR';
      isoMoeda = 'ARS';

      break;
    case FORMA_PESO_CHILENO:
      abrevMoeda = 'es-CL';
      isoMoeda = 'CLP';

      break;
  };

  const objMoeda = {
    abrevMoeda: abrevMoeda,
    isoMoeda: isoMoeda
  };

  return objMoeda;
};

/* Formata o valor monetário no padrão brasileiro sem símbolo da moeda */
export function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value).replace(/[^\d,.-]/g, ''); // remove o símbolo da moeda
};

/* Retorna a descrição da moeda */
export function getDescFormaPgto(formapgto) {
  let descFormaPgto = '';

  switch (parseInt(formapgto, 10)) {
    case FORMA_CORTESIA:
      descFormaPgto = 'Cortesia'

      break;
    case FORMA_DINHEIRO:
      descFormaPgto = 'BRL';

      break;
    case FORMA_DOLAR:
      descFormaPgto = 'USD';

      break;
    case FORMA_EURO:
      descFormaPgto = 'EUR';

      break;
    case FORMA_GUARANI:
      descFormaPgto = 'PYG';

      break;
    case FORMA_PESO:
      descFormaPgto = 'ARS';

      break;
    case FORMA_PRAZO:
      descFormaPgto = 'Prazo'

      break;
  };

  return descFormaPgto;
};

/* Formata a data */
export function formatData(data) {
  if (!data) return ''; // retorna a string vazia se for null, undefined ou ''

  const dateObject = new Date(data);

  if (Number.isNaN(dateObject.getTime())) return '';

  return dateObject.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/* Formata a data para o padrão aceito por inputs type="date" */
export function formatDateForInput(dateString) {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = (`0${date.getMonth() + 1}`).slice(-2);
  const day = (`0${date.getDate()}`).slice(-2);

  return `${year}-${month}-${day}`;
};

/* Retorna o valor seguro para inputs do tipo date */
export function getDateInputValue(value) {
  return value ? String(value).substring(0, 10) : '';
};

/* Valida o CPF */
export function validarCPF(cpf) {
  cpf = cpf.replace(/[^\d]+/g, ''); // remove os caracteres especiais

  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false; // verifica se tem 11 dígitos ou todos iguais
  }

  let soma = 0;
  let resto;

  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }

  resto = (soma * 10) % 11;

  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.substring(9, 10))) return false;

  soma = 0;

  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }

  resto = (soma * 10) % 11;

  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.substring(10, 11))) return false;

  return true;
};

/* Formata a data e a hora conforme o locale do navegador */
export function formatDateTimeISO(isoString) {
  const date = new Date(isoString);

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,  // para evitar a exibição em formato AM/PM
  }).replace(',', ''); // remove a vírgula entre a data e a hora
};

/* Valida o campo e o valor com base no schema informado */
export function validateField(field, value, schema) {
  let error = '';

  const fieldSchema = schema[field];
  const type = fieldSchema.type || 'string';

  const isEmptyValue = (val) => {
    return (
      val === undefined ||
      val === null ||
      (typeof val === 'string' && val.trim() === '') ||
      (typeof val === 'number' && Number.isNaN(val)) ||
      (typeof val === 'boolean' && val === undefined) ||
      (type === 'select-one' && val === undefined) ||
      (type === 'date' && val === undefined) ||
      (type === 'date' && val === '')
    );
  };

  // verifica se o campo é obrigatório e se está vazio
  if (fieldSchema.presence && !fieldSchema.presence.allowEmpty && isEmptyValue(value)) {
    error = fieldSchema.presence.message;
  }

  // validação de comprimento - apenas verifica o comprimento se o valor for string
  if (!error && fieldSchema.length && typeof value === 'string') {
    if (value.length > fieldSchema.length.maximum) {
      error = `O campo ${field} deve ter no máximo ${fieldSchema.length.maximum} caracteres`;
    }
  }

  // valida comprimento para números no caso do tipo 'integer' (antes de converter para número)
  if (!error && type === 'integer' && !isEmptyValue(value) && fieldSchema.length) {
    const maxDigits = fieldSchema.length.maximum;
    const maxValue = Math.pow(10, maxDigits) - 1;  // calcula o maior valor permitido com base no número de dígitos

    if (value > maxValue) {
      error = `O campo ${field} não pode ter mais que ${maxDigits} dígitos`;
    }
  }

  // converte para número se o tipo for 'integer' e o valor não estiver vazio e sem erros de comprimento
  if (!error && type === 'integer' && !isEmptyValue(value)) {
    value = parseInt(value, 10);
  }

  // verifica se há erro e continua com a validação de tipo se o valor não for vazio
  if (!error && !isEmptyValue(value)) {
    switch (type) {
      case 'integer':
        if (typeof value !== 'number' || Number.isNaN(value) || !Number.isInteger(value)) {
          error = `O campo ${field} deve ser um número inteiro válido!`;
        }

        break;
      case 'number':
        if (typeof value !== 'number' || Number.isNaN(value)) {
          error = `O campo ${field} deve ser um número válido!`;
        }

        break;
      case 'string':
        if (typeof value !== 'string') {
          error = `O campo ${field} deve ser uma string válida!`;
        }

        break;
      case 'select-one':
        if (typeof value !== 'number' || Number.isNaN(value) || !Number.isInteger(value)) {
          error = `O campo ${field} deve ser escolhido!`;
        }

        break;
      case 'checkbox':
        if (typeof value !== 'boolean') {
          error = `O campo ${field} deve ser um valor booleano (true/false)!`;
        }

        break;
      case 'date':
        if (!(isValidDate(value))) {
          error = `O campo ${field} deve ser um valor data!`;
        }

        break;
      default:
        error = `Tipo de campo inválido para ${field}!`;
        break;
    };
  }

  return error;
};

/* Valida se uma string é uma data válida */
const isValidDate = (dateString) => {
  const date = new Date(dateString);

  return !Number.isNaN(date.getTime());
};

/* Gera a senha aleatória */
export function generateRandomPwd(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*_';
  let pwd = '';

  for (let i = 0; i < length; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return pwd;
};

/* Retorna o código do usuário que registrou */
export function getCodUsuarioRegistrou() {
  const codusuarioregistrou = sessionStorage.getItem('codUser') || localStorage.getItem('codUser');

  return codusuarioregistrou ? Number(codusuarioregistrou) : null;
};

/* Verifica se um valor está ausente, mas permite 0 (zero) como válido */
export function isMissingValue(value) {
  return value === undefined || value === null || value === '';
};

/* Cria um FormData e adiciona os campos informados */
export function appendFieldsToFormData(fields = {}) {
  const formData = new FormData();

  Object.entries(fields).forEach(([field, value]) => {
    formData.append(field, value);
  });

  return formData;
};

/* Formata os campos do tipo hora */
export function formatTime(value) {
  if (!value) {
    return '';
  }

  const stringValue = String(value);

  // se o valor já vem só como hora (sem data/timezone, ex.: coluna TIME do Firebird),
  // não tem conversão de fuso envolvida - extrai direto
  const apenasHora = !stringValue.includes('T') && !stringValue.includes('-');

  if (apenasHora) {
    const match = stringValue.match(/(\d{2}):(\d{2})/);

    return match ? `${match[1]}:${match[2]}` : '';
  }

  // é timestamp completo (ISO, com data) - o backend manda em UTC corretamente,
  // então aqui precisa converter pra hora local mesmo
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

/* Retorna o valor seguro para inputs */
export function getInputValue(value, defaultValue = '') {
  return value !== null && value !== undefined ? value : defaultValue;
};

/* Retorna o valor monetário seguro para inputs */
export function getCurrencyInputValue(value, defaultValue = '0,00') {
  return value !== '' && value !== null && value !== undefined ? formatCurrency(value) : defaultValue;
};

/* Retorna apenas os dígitos de um valor */
export function getOnlyDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
};

/* Converte o valor monetário digitado para número */
export function parseCurrencyInputValue(value) {
  const semFormatacao = String(value || '').replace(/[^\d,]/g, '');
  const numero = parseFloat(semFormatacao.replace(',', '.'));

  return !Number.isNaN(numero) ? numero : '';
};